import { fromJS, Map } from 'immutable'
import { getIn, mergeDeep } from 'utils/immutable'
import { isNil, compose } from 'ramda'
import * as ctpActions from 'actions/ctp.js'
import { UPDATE_EXM, ADD_XMC } from 'actions/truck.js'
import * as dataTypes from 'utils/constants'
import { buildVehicleModules, makePinUriFromCircuit } from 'utils/circuits'
import { createReducer } from 'reducers/utils'
import { uriIt } from 'utils/utils'

export const initialState = fromJS({
  isSearching: false,
  isEditing: false,
  isSaving: false,
  showMapConfirmationModal: false,
  showPinsNotAvailableModal: false,
  serialNumber: '', // Vehicle selected for editing by the user
  vehicleModules: fromJS([]), // Module configuration of the vehicle
  modules: {},
  moduleConfiguration: {},
  ctpReport: {
    isSearching: false,
    asBuilt: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: [],
    },
    asServiced: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: [],
    },
  },
  selectedRP: {},
  availableCircuits: Map(), // Circuits available for the current RP
  connectorPins: [], // short term cache of connector pin metadata, array of pinDetails objects
  assignments: fromJS([]), // short term cache of CTP assignments, a buffer until they can be saved.
  hoveredPin: {
    // pin that has been hovered over
    location: '',
    currentLimit: '',
    description: '',
    commonNodeSiblings: [], // this may need to be reset as an immutable collection
    module: '',
    connector: '',
  },
  hoveredCircuit: {
    // Circuit assigned to the currently hovered pin
    circuitNumber: '',
    currentLimit: '',
    description: '',
  },
  detailedCircuits: {},
  isRequestingCircuitRevisions: false,
  detailedBundles: {},
  isRequestingBundleRevisions: false,
})

const handlers = {
  [ctpActions.CLEAR_CTP_ASSIGNMENT_STATE]: clearCtpAssignmentState,
  [ctpActions.CLEAR_PIN_ASSIGNMENTS]: clearPinAssignments,
  [ctpActions.FETCHING_VEHICLE_CTP]: fetchingVehicleCtp,
  [ctpActions.NO_SERVICE_RECORDS_FOR_VEHICLE]: noServiceRecordsForVehicle,
  [ctpActions.RECEIVED_VEHICLE_CTP]: receivedVehicleCtp,
  [ctpActions.GET_VEHICLE_CTP]: getVehicleCtp,
  [ctpActions.MOVE_CIRCUIT_SUCCESS]: moveCircuitSuccess,
  [ctpActions.REMOVE_CIRCUIT_SUCCESS]: removeCircuitSuccess,
  [ctpActions.RP_CIRCUITS_RECEIVED]: rpCircuitsReceived,
  [ctpActions.RETRIEVED_CIRCUIT_AND_PIN_DATA]: retrievedCircuitAndPinData,
  [ctpActions.SET_SELECTED_RP]: setSelectedRp,
  [ctpActions.CLEAR_SELECTED_RP]: clearSelectedRp,
  [ctpActions.SELECT_PIN]: selectPin,
  [ctpActions.SELECT_PIN_FOR_ASSIGNMENT]: selectPinForAssignment,
  [ctpActions.CLEAR_PIN_FOR_ASSIGNMENT]: clearPinForAssignment,
  [ctpActions.CLEAR_HOVERED_PIN]: clearHoveredPin,
  [ctpActions.SET_HOVERED_PIN]: setHoveredPin,
  [ctpActions.FETCHING_VEHICLE_CTP_REPORT]: fetchingVehicleCtpReport,
  [ctpActions.RECEIVED_VEHICLE_CTP_REPORT]: receivedVehicleCtpReport,
  [ctpActions.GET_VEHICLE_CTP_REPORT]: getVehicleCtpReport,
  [ctpActions.NO_VEHICLE_CTP_REPORT]: noVehicleCtpReport,
  [ctpActions.TOGGLE_SELECTED_CTP_REPORT_AS_SERVICED_PIN]:
    toggleSelectedCtpReportAsServicedPin,
  [ctpActions.TOGGLE_SELECTED_CTP_REPORT_AS_BUILT_PIN]:
    toggleSelectedCtpReportAsBuiltPin,
  [ctpActions.UPDATE_CIRCUIT_MAPPED_STATUS]: updateCircuitMappedStatus,
  [ctpActions.CONFIRM_CIRCUIT_MAPPING]: confirmCircuitMapping,
  [ctpActions.CIRCUIT_MAPPING_CONFIRMED]: circuitMappingConfirmed,
  [ctpActions.NO_PINS_AVAILABLE_FOR_CIRCUIT]: noPinsAvailableForCircuit,
  [ctpActions.NO_PINS_AVAILABLE_ACKNOWLEDGED]: noPinsAvailableAcknowledged,
  [ctpActions.BUNDLE_DETAILS_RECEIVED]: bundleDetailsReceived,
  [ctpActions.BUNDLE_REVISIONS_RECEIVED]: bundleRevisionsReceived,
  [ctpActions.CIRCUIT_DETAILS_RECEIVED]: circuitDetailsReceived,
  [ctpActions.CIRCUIT_REVISIONS_RECEIVED]: circuitRevisionsReceived,
  [ctpActions.CIRCUIT_BUNDLES_RECEIVED]: circuitBundlesReceived,
  [ADD_XMC]: getVehicleXmcCtp,
  [UPDATE_EXM]: modifyVehicleEXM,
}

export default createReducer(initialState, handlers)

function mergeRevisions(
  state,
  mainDataType, // e.g. detailedBundles
  action,
  subType, // ID of the mainDataType in action data
  statusName
) {
  // e.g. isRequestingCircuitRevisions
  // Completely replace the revision array.
  const newState = state.mergeIn(
    [mainDataType, action[subType]],
    fromJS({
      revisions: action.revisions,
    })
  )
  if (newState == null) {
    // Should not happen.
    // Log.log(`state missing $(mainDataType)[$(action[subType])]`)
    return state
  }
  return newState.set(statusName, false)
}

function clearCtpAssignmentState(state, action) {
  return state.merge({
    isSearching: false,
    isEditing: false,
    isSaving: false,
    showMapConfirmationModal: false,
    showPinsNotAvailableModal: false,
    modules: {},
    selectedRP: {},
    availableCircuits: Map(),
    connectorPins: [],
    assignments: fromJS([]),
    selectedCircuit: {
      circuitNumber: '',
      currentLimit: '',
      description: '',
    },
    hoveredPin: {
      location: '',
      currentLimit: '',
      description: '',
      commonNodeSiblings: [], // this may need to be reset as an immutable collection
      module: '',
      connector: '',
    },
    hoveredCircuit: {
      circuitNumber: '',
      currentLimit: '',
      description: '',
    },
    isHovered: false,
    detailedCircuits: {},
    detailedBundles: {},
  })
}

function clearPinAssignments(state) {
  return state.set('assignments', fromJS([]))
}

function fetchingVehicleCtp(state) {
  return state.set('isSearching', true)
}

function noServiceRecordsForVehicle(state, action) {
  if (!action.serialNumber) return state
  return state.merge({
    isSearching: false,
    serialNumber: action.serialNumber,
    vehicleModules: fromJS([]),
  })
}

function receivedVehicleCtp(state, action) {
  if (!action.serialNumber) return state

  const modules = action.modules || []

  return state.merge({
    isSaving: false,
    isEditing: false,
    isSearching: false,
    serialNumber: action.serialNumber,
    selectedPin: {
      location: '',
      currentLimit: '',
      description: '',
      selected: false,
      circuitNumber: '',
    },
    vehicleModules: fromJS(modules),
  })
}

function modifyVehicleEXM(state, action) {
  const {
    serialNumber,
    data: {
      entities,
      result: { modules },
    },
  } = action
  const payload = { modules, ...buildVehicleModules(entities) }
  return state.mergeDeepIn(['modules', serialNumber], payload)
}

function moveCircuitSuccess(state, action) {
  const { serialNumber, source, data } = action

  const { entities } = data

  const { modules } = entities

  const vehicleModulesPath = ['modules', serialNumber]

  const modulesNames = Object.keys(modules)

  const vehicleModules = {
    modules: modulesNames,
    ...buildVehicleModules(entities),
  }

  const sourcePinLookupKey = makePinUriFromCircuit(source)

  const makeNewSourceCircuit = compose(getIn(vehicleModulesPath))

  const mergeDeepNewModules = compose(
    mergeDeep(vehicleModules),
    getIn(vehicleModulesPath)
  )

  const circuitList = makeNewSourceCircuit(state)
  const newSource = circuitList.get(sourcePinLookupKey).delete('circuitNumber')
  const newModules = mergeDeepNewModules(state)

  const newSourceMergedInNewModules = newModules.set(
    sourcePinLookupKey,
    newSource
  )

  return state.setIn(vehicleModulesPath, newSourceMergedInNewModules)
}

function getVehicleCtp(state, action) {
  const {
    serialNumber,
    data: { entities, result },
  } = action

  const payload = { modules: result, ...buildVehicleModules(entities) }
  return state
    .mergeDeepIn(['modules', serialNumber], payload)
    .mergeDeepIn(['moduleConfiguration', serialNumber], entities)
}

function getVehicleXmcCtp(state, action) {
  const {
    serialNumber,
    data: { result = [], entities },
  } = action

  const payload = { modules: result, ...buildVehicleModules(entities) }
  return state
    .mergeDeepIn(['modules', serialNumber], payload)
    .mergeDeepIn(['moduleConfiguration', serialNumber], entities)
}

function rpCircuitsReceived(state, action) {
  const { circuits } = action

  if (!circuits) return state

  const availableCircuitsByRp = circuits.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.rpUri]: curr.circuits,
    }),
    {}
  )

  return state.merge({
    isSearching: false,
    availableCircuits: fromJS(availableCircuitsByRp),
  })
}

function removeCircuitSuccess(state, action) {
  const { serialNumber, module, connector, pin, data } = action

  const {
    entities: { pins },
  } = data

  const targetUri = uriIt(module, connector, pin)
  const newTarget = pins[targetUri]

  // TODO: new target needs to be set as an immutable object
  return state.setIn(['modules', serialNumber, targetUri], newTarget)
}

// TODO: write a test for this reducer
function retrievedCircuitAndPinData(state, action) {
  const {
    serialNumber,
    entities: { pins, circuits, features },
    result: { pins: resultPins },
  } = action
  if (circuits) {
    const pinsWithCircuitNumbers = Object.entries(pins).reduce(
      (acc, [key, value]) => {
        const pinWithCircuit = resultPins.find(
          ({ pin, circuit }) => pin === key && circuit
        )
        return {
          ...acc,
          [key]: {
            ...value,
            ...(pinWithCircuit && {
              circuitNumber: pinWithCircuit.circuit,
            }),
          },
        }
      },
      {}
    )
    return (
      state
        // This needs to be mergeDeepIn to keep existing keys like commonNodeSiblings
        .mergeDeepIn(
          ['modules', serialNumber],
          fromJS({ ...pinsWithCircuitNumbers, ...circuits })
        )
        .set('isSearching', false)
        .set('connectorPins', pins)
    )
  }

  const updateModulePins = (newPins) => (modules) => {
    // if modules don't exist, set them
    if (!modules) return newPins

    // we always want to ensure features are pulled in fresh
    return modules.map((module, key) => {
      const newPinData = newPins.get(key)

      if (!newPinData) return module.delete('features')

      return newPinData.merge(module.delete('features'))
    })
  }

  return state
    .updateIn(['modules', serialNumber], updateModulePins(fromJS(pins)))
    .setIn(['modules', serialNumber, 'features'], fromJS(features || []))
    .set('isSearching', false)
    .set('connectorPins', pins)
}

function setSelectedRp(state, action) {
  if (!action.rp) return state

  return state.set('selectedRP', fromJS(action.rp))
}

function clearSelectedRp(state) {
  return state.set('selectedRP', fromJS({}))
}

function selectPin(state, action) {
  const { pin, circuit } = action

  let newState = {
    selectedPin: fromJS({
      location: pin.location,
      currentLimit: pin.currentLimit,
      description: pin.description,
      pinType: pin.pinType || '',
      circuitNumber: circuit ? circuit.circuitNumber : '',
    }),
  }

  if (action.includeCircuit) {
    // TODO: also assign the circuit object as a child of the selectedPin, not just its sibling
    newState = {
      ...newState,
      selectedCircuit: fromJS({
        circuitNumber: circuit ? circuit.circuitNumber : '',
        currentLimit: circuit ? circuit.currentLimit : '',
        description: circuit
          ? circuit.description || circuit.circuitDescription
          : '',
      }),
    }
  }

  return state.merge(newState)
}

function selectPinForAssignment(state, { pin }) {
  if (isNil(pin)) return state

  return state.merge(
    fromJS({
      targetPin: pin,
    })
  )
}

function clearPinForAssignment(state) {
  const newState = {
    targetPin: fromJS({
      key: '',
      location: '',
      pinType: '',
      connectorName: '',
      moduleName: '',
      circuitNumber: '',
      current: '#',
    }),
  }
  return state.merge(newState)
}

function clearHoveredPin(state) {
  return state.merge({
    hoveredPin: fromJS({
      location: '',
      commonNodeSiblings: [],
    }),
  })
}

function setHoveredPin(state, action) {
  return state.merge({
    hoveredPin: fromJS(action.pin),
  })
}

function fetchingVehicleCtpReport(state) {
  return state.setIn(['ctpReport', 'isSearching'], true)
}

function receivedVehicleCtpReport(state, action) {
  if (!action.serialNumber || !action.ctpReport) return state

  const asBuilt_ctp = action.ctpReport.asBuilt.ctp.map((row) => ({
    ...row,
    key: `${row.connector}-${row.pin}-${row.circuitNumber}`,
    selected: false,
  }))

  const asServiced_ctp = !action.ctpReport.asServiced
    ? []
    : action.ctpReport.asServiced.ctp.map((row) => ({
        ...row,
        key: `${row.connector}-${row.pin}-${row.circuitNumber}`,
        selected: false,
      }))

  const ctpReportObject = {
    isSearching: false,
    asBuilt: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: asBuilt_ctp,
    },
    asServiced: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: asServiced_ctp,
    },
  }

  return state.merge({
    isSearching: false,
    serialNumber: action.serialNumber,
    ctpReport: fromJS(ctpReportObject),
  })
}

function getVehicleCtpReport(state, { data, serialNumber }) {
  const { asBuilt, asServiced } = data

  const asBuilt_ctp = asBuilt.ctp.map((row) => ({
    ...row,
    key: `${row.connector}-${row.pin}-${row.circuitNumber}`,
    selected: false,
  }))

  const asServiced_ctp = !asServiced
    ? []
    : asServiced.ctp.map((row) => ({
        ...row,
        key: `${row.connector}-${row.pin}-${row.circuitNumber}`,
        selected: false,
      }))

  const ctpReportObject = {
    asBuilt: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: asBuilt_ctp,
    },
    asServiced: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
        key: '',
      },
      ctp: asServiced_ctp,
    },
  }

  return state.merge({
    serialNumber,
    ctpReport: fromJS(ctpReportObject),
  })
}

function noVehicleCtpReport(state, action) {
  const ctpReportObject = {
    isSearching: false,
    asBuilt: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
      },
      ctp: [],
    },
    asServiced: {
      currentRow: {
        connector: '',
        pin: '',
        circuitNumber: '',
      },
      ctp: [],
    },
  }

  return state.merge({
    isSearching: false,
    serialNumber: action.serialNumber,
    ctpReport: fromJS(ctpReportObject),
  })
}

function toggleSelectedCtpReportAsServicedPin(state, action) {
  const ctpReport = state.get('ctpReport').toJS()

  const deselected = ctpReport.asServiced.ctp.map((row) => ({
    ...row,
    selected: false,
  }))
  ctpReport.asServiced.ctp = deselected

  const location = ctpReport.asServiced.ctp.findIndex(
    (row) => row.key === action.currentRow.key
  )
  ctpReport.asServiced.ctp[location].selected = true
  ctpReport.asServiced.currentRow = action.currentRow

  return state.merge({
    ctpReport: fromJS(ctpReport),
  })
}

function toggleSelectedCtpReportAsBuiltPin(state, action) {
  const ctpReport = state.get('ctpReport').toJS()

  const deselected = ctpReport.asBuilt.ctp.map((row) => ({
    ...row,
    selected: false,
  }))
  ctpReport.asBuilt.ctp = deselected

  const index = ctpReport.asBuilt.ctp.findIndex(
    (row) => row.key === action.currentRow.key
  )
  ctpReport.asBuilt.ctp[index].selected = true
  ctpReport.asBuilt.currentRow = action.currentRow

  return state.merge({
    ctpReport: fromJS(ctpReport),
  })
}

function updateCircuitMappedStatus(state, { rpUri, circuitsThatNeedMapping }) {
  return state.setIn(['availableCircuits', rpUri], circuitsThatNeedMapping)
}

function confirmCircuitMapping(state) {
  return state.set('showMapConfirmationModal', true)
}

function circuitMappingConfirmed(state) {
  return state.set('showMapConfirmationModal', false)
}

function noPinsAvailableForCircuit(state) {
  return state.set('showPinsNotAvailableModal', true)
}

function noPinsAvailableAcknowledged(state) {
  return state.set('showPinsNotAvailableModal', false)
}

function bundleDetailsReceived(state, action) {
  const { data, bundleNumber } = action

  // Note NOT mergeDeep() - we only save latest bundle and
  // do not cache previously viewed ones - always refetches.
  return state.merge({
    isSearching: false,
    detailedBundles: fromJS({
      [bundleNumber]: data,
    }),
  })
}

function bundleRevisionsReceived(state, action) {
  return mergeRevisions(
    state,
    dataTypes.RESOURCE_BUNDLE_DETAILS,
    action,
    'bundleNumber',
    'isRequestingBundleRevisions'
  )
}

function circuitDetailsReceived(state, action) {
  const { circuitNumber, data } = action
  return state.mergeIn(['detailedCircuits', circuitNumber], fromJS(data))
}

function circuitRevisionsReceived(state, action) {
  return mergeRevisions(
    state,
    dataTypes.RESOURCE_CIRCUIT_DETAILS,
    action,
    'circuitNumber',
    'isRequestingCircuitRevisions'
  )
}

function circuitBundlesReceived(state, action) {
  const { circuitNumber, data } = action
  return state.setIn(
    ['detailedCircuits', circuitNumber, 'bundles'],
    fromJS(data)
  )
}
